import React, { useMemo, useState, useEffect } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import api from './api';
import {
    MATCON_CATEGORIES,
    OOR_SUB_STATUSES,
    filterStatusRecords,
    countByMatconCategory,
    countBySiteAndMatcon,
    countOorBreakdownBySite,
    computeAwaitingPartsMetrics,
    generateSimulatedTrend,
    buildTrendFromSnapshots,
    computeMcChangeFromSnapshots,
    loadMcChangeSnapshot,
    computeMcChange,
    localDateString,
    shiftDateString,
    liveRecordsToBriefRows,
    filterBriefRows,
    countBriefBySite,
    countBriefByStatus,
    countBriefByGroup,
    buildBriefNestedGroups,
} from './matconUtils';

const MC_READINESS_GOAL = 165;
const MC_READINESS_GAUGE_MIN = 100;
const MC_READINESS_GAUGE_MAX = 185;

const MC_READINESS_ZONES = [
    { from: 100, to: 125, color: '#1a1a1a' },
    { from: 125, to: 150, color: '#dc3545' },
    { from: 150, to: 165, color: '#ffc107' },
    { from: 165, to: 185, color: '#28a745' },
];

const getStyles = (theme) => {
    const isDark = theme === 'dark';
    return {
        wrapper: { marginBottom: '30px' },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '16px',
            marginBottom: '20px',
        },
        title: {
            fontSize: '22px',
            fontWeight: 700,
            color: isDark ? '#e0e0e0' : '#333',
            margin: 0,
        },
        subtitle: { fontSize: '12px', color: isDark ? '#888' : '#666', marginTop: '4px' },
        filters: { display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' },
        filterLabel: { fontSize: '13px', color: isDark ? '#a0a0a0' : '#666', fontWeight: 600 },
        select: {
            padding: '6px 10px',
            borderRadius: '4px',
            border: `1px solid ${isDark ? '#555' : '#ccc'}`,
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            color: isDark ? '#e0e0e0' : '#333',
            fontSize: '13px',
        },
        timestamps: {
            textAlign: 'right',
            fontSize: '11px',
            color: isDark ? '#888' : '#666',
            lineHeight: 1.5,
        },
        topRow: {
            display: 'grid',
            gridTemplateColumns: 'minmax(220px, 1fr) minmax(280px, 1.2fr) minmax(320px, 1.4fr)',
            gap: '16px',
            marginBottom: '16px',
        },
        bottomRow: {
            display: 'grid',
            gridTemplateColumns: 'minmax(280px, 1fr) minmax(400px, 1.6fr)',
            gap: '16px',
        },
        card: {
            backgroundColor: isDark ? '#1e1e1e' : '#fff',
            border: `1px solid ${isDark ? '#444' : '#ddd'}`,
            borderRadius: '8px',
            padding: '16px',
            boxShadow: `0 2px 5px ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`,
        },
        cardTitle: {
            fontSize: '14px',
            fontWeight: 700,
            marginBottom: '12px',
            color: isDark ? '#e0e0e0' : '#333',
        },
        kpiRow: {
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
        },
        kpiCard: (bg) => ({
            backgroundColor: bg,
            borderRadius: '6px',
            padding: '12px 8px',
            textAlign: 'center',
            color: '#fff',
        }),
        kpiValue: { fontSize: '1.6rem', fontWeight: 700, lineHeight: 1.1 },
        kpiLabel: { fontSize: '10px', marginTop: '4px', opacity: 0.95, fontWeight: 600 },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '12px',
        },
        th: {
            padding: '8px 6px',
            textAlign: 'center',
            borderBottom: `2px solid ${isDark ? '#555' : '#ccc'}`,
            color: isDark ? '#ccc' : '#444',
            fontWeight: 700,
        },
        td: {
            padding: '6px',
            textAlign: 'center',
            borderBottom: `1px solid ${isDark ? '#333' : '#eee'}`,
            color: isDark ? '#e0e0e0' : '#333',
        },
        tdLabel: {
            textAlign: 'left',
            fontWeight: 600,
            paddingLeft: '4px',
        },
        tdTotal: { fontWeight: 700 },
        tdSubLabel: {
            textAlign: 'left',
            fontWeight: 500,
            paddingLeft: '28px',
            color: isDark ? '#a0a0a0' : '#555',
            fontSize: '11px',
        },
        expandBtn: {
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '20px',
            height: '20px',
            marginRight: '6px',
            padding: 0,
            border: 'none',
            background: 'transparent',
            color: isDark ? '#ccc' : '#444',
            cursor: 'pointer',
            fontSize: '10px',
            verticalAlign: 'middle',
        },
        matconLabelCell: {
            display: 'flex',
            alignItems: 'center',
        },
        trendNote: {
            fontSize: '11px',
            color: isDark ? '#888' : '#777',
            fontStyle: 'italic',
            marginTop: '8px',
        },
        supplyKpiRow: {
            display: 'grid',
            gridTemplateColumns: 'repeat(4, 1fr)',
            gap: '8px',
            marginTop: '12px',
        },
        gaugeWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center' },
        gaugeValue: {
            fontSize: '2.2rem',
            fontWeight: 700,
            fill: isDark ? '#e0e0e0' : '#333',
        },
        gaugeLabel: {
            fontSize: '12px',
            fill: isDark ? '#a0a0a0' : '#666',
        },
        briefCard: {
            backgroundColor: isDark ? '#1e1e1e' : '#fff',
            border: `1px solid ${isDark ? '#444' : '#ddd'}`,
            borderRadius: '8px',
            padding: '16px',
            boxShadow: `0 2px 5px ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)'}`,
            marginTop: '16px',
        },
        briefHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
            gap: '12px',
            marginBottom: '12px',
        },
        briefTitle: {
            fontSize: '14px',
            fontWeight: 700,
            color: isDark ? '#e0e0e0' : '#333',
            margin: 0,
        },
        briefSubtitle: {
            fontSize: '11px',
            color: isDark ? '#888' : '#666',
            marginTop: '4px',
        },
        siteStrip: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
            gap: '8px',
            marginBottom: '12px',
        },
        siteChip: {
            backgroundColor: isDark ? '#2a2a2a' : '#f5f5f5',
            border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
            borderRadius: '6px',
            padding: '8px 10px',
            textAlign: 'center',
        },
        siteChipLabel: {
            fontSize: '11px',
            color: isDark ? '#a0a0a0' : '#666',
            fontWeight: 600,
        },
        siteChipValue: {
            fontSize: '1.3rem',
            fontWeight: 700,
            color: isDark ? '#e0e0e0' : '#222',
            lineHeight: 1.2,
        },
        statusGroup: {
            borderTop: `1px solid ${isDark ? '#333' : '#eee'}`,
        },
        statusGroupHeader: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            padding: '10px 4px',
            border: 'none',
            background: 'transparent',
            color: isDark ? '#e0e0e0' : '#333',
            cursor: 'pointer',
            textAlign: 'left',
            fontSize: '13px',
            fontWeight: 700,
        },
        statusChildHeader: {
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            padding: '8px 4px 8px 22px',
            border: 'none',
            background: 'transparent',
            color: isDark ? '#d0d0d0' : '#333',
            cursor: 'pointer',
            textAlign: 'left',
            fontSize: '12px',
            fontWeight: 600,
        },
        statusChildBlock: {
            borderTop: `1px solid ${isDark ? '#2a2a2a' : '#f0f0f0'}`,
        },
        aircraftTableWrap: {
            paddingLeft: '28px',
            marginBottom: '4px',
        },
        statusCount: {
            marginLeft: '8px',
            color: isDark ? '#aaa' : '#666',
            fontWeight: 500,
        },
        statusDelta: {
            marginLeft: '8px',
            fontSize: '12px',
            fontWeight: 600,
        },
        aircraftTable: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '12px',
            marginBottom: '8px',
        },
        aircraftTh: {
            padding: '6px 8px',
            textAlign: 'left',
            borderBottom: `1px solid ${isDark ? '#444' : '#ddd'}`,
            color: isDark ? '#aaa' : '#555',
            fontWeight: 700,
        },
        aircraftTd: {
            padding: '6px 8px',
            textAlign: 'left',
            borderBottom: `1px solid ${isDark ? '#2a2a2a' : '#f0f0f0'}`,
            color: isDark ? '#e0e0e0' : '#333',
            verticalAlign: 'top',
        },
        emptyNote: {
            fontSize: '12px',
            color: isDark ? '#888' : '#777',
            fontStyle: 'italic',
            padding: '8px 4px',
        },
        emptyNoteChild: {
            fontSize: '12px',
            color: isDark ? '#888' : '#777',
            fontStyle: 'italic',
            padding: '4px 4px 8px 44px',
        },
    };
};

function McReadinessGauge({ mc, min, max, target, theme }) {
    const isDark = theme === 'dark';
    const range = Math.max(max - min, 1);

    const valueToAngle = (value) => {
        const clamped = Math.min(max, Math.max(min, value));
        return -180 + ((clamped - min) / range) * 180;
    };

    const needleAngle = valueToAngle(mc);
    const targetAngle = valueToAngle(target);

    const cx = 100;
    const cy = 95;
    const r = 75;

    const polar = (angleDeg, radius) => {
        const rad = (angleDeg * Math.PI) / 180;
        return { x: cx + radius * Math.cos(rad), y: cy + radius * Math.sin(rad) };
    };

    const arcPath = (startDeg, endDeg) => {
        const start = polar(startDeg, r);
        const end = polar(endDeg, r);
        const large = endDeg - startDeg > 180 ? 1 : 0;
        return `M ${start.x} ${start.y} A ${r} ${r} 0 ${large} 1 ${end.x} ${end.y}`;
    };

    const needleEnd = polar(needleAngle, r - 12);
    const zones = MC_READINESS_ZONES.map((zone) => ({
        from: valueToAngle(zone.from),
        to: valueToAngle(zone.to),
        color: zone.color,
    }));

    return (
        <svg viewBox="0 0 200 120" width="100%" style={{ maxWidth: 260 }}>
            {zones.map((z) => (
                <path
                    key={`${z.from}-${z.to}`}
                    d={arcPath(z.from, z.to)}
                    fill="none"
                    stroke={z.color}
                    strokeWidth={14}
                    strokeLinecap="butt"
                />
            ))}
            <line
                x1={polar(targetAngle, r - 20).x}
                y1={polar(targetAngle, r - 20).y}
                x2={polar(targetAngle, r + 4).x}
                y2={polar(targetAngle, r + 4).y}
                stroke={isDark ? '#fff' : '#333'}
                strokeWidth={2}
                strokeDasharray="3 2"
            />
            <line x1={cx} y1={cy} x2={needleEnd.x} y2={needleEnd.y} stroke={isDark ? '#f0f0f0' : '#222'} strokeWidth={3} />
            <circle cx={cx} cy={cy} r={5} fill={isDark ? '#f0f0f0' : '#222'} />
            <text x={cx} y={cy + 28} textAnchor="middle" className="gauge-value" style={{ fontSize: 28, fontWeight: 700, fill: isDark ? '#e0e0e0' : '#333' }}>
                {mc}
            </text>
            <text x={polar(-180, r + 14).x} y={polar(-180, r + 14).y + 4} textAnchor="middle" style={{ fontSize: 9, fill: isDark ? '#888' : '#666' }}>{min}</text>
            <text x={polar(0, r + 14).x} y={polar(0, r + 14).y + 4} textAnchor="middle" style={{ fontSize: 9, fill: isDark ? '#888' : '#666' }}>{max}</text>
        </svg>
    );
}

function formatDelta(delta) {
    if (delta == null) return null;
    if (delta === 0) return '0 vs WoW';
    return `${delta > 0 ? '+' : ''}${delta} vs WoW`;
}

function MocDailyCallDashboard({ theme, statusData, nmcsPmcsData = [], lastRefreshed }) {
    const styles = getStyles(theme);
    const [selectedTms, setSelectedTms] = useState('All');
    const [oorExpanded, setOorExpanded] = useState(false);
    const [historySnapshots, setHistorySnapshots] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [briefDate, setBriefDate] = useState(localDateString());
    const [availableBriefDates, setAvailableBriefDates] = useState([]);
    const [snapshotBriefRows, setSnapshotBriefRows] = useState([]);
    const [priorWeekRows, setPriorWeekRows] = useState([]);
    const [briefLoading, setBriefLoading] = useState(false);
    const [hasSnapshotForDate, setHasSnapshotForDate] = useState(false);
    const [expandedBriefGroups, setExpandedBriefGroups] = useState(() => new Set(['MCRS', 'OOR']));
    const [expandedBriefStatuses, setExpandedBriefStatuses] = useState(() => new Set(['PM', 'DN', 'AWP']));

    const tmsOptions = useMemo(() => {
        const set = new Set(statusData.map((r) => r.tms || r.aircraft?.tms).filter(Boolean));
        return ['All', ...Array.from(set).sort()];
    }, [statusData]);

    const filteredRecords = useMemo(
        () => filterStatusRecords(statusData, { tms: selectedTms }),
        [statusData, selectedTms]
    );

    const counts = useMemo(() => countByMatconCategory(filteredRecords), [filteredRecords]);
    const siteBreakdown = useMemo(() => countBySiteAndMatcon(filteredRecords), [filteredRecords]);
    const oorBreakdown = useMemo(() => countOorBreakdownBySite(filteredRecords), [filteredRecords]);
    const siteKeys = useMemo(
        () => Object.keys(siteBreakdown.bySite || {}).sort((a, b) => a.localeCompare(b)),
        [siteBreakdown],
    );

    const filteredNmcs = useMemo(() => {
        if (selectedTms === 'All') return nmcsPmcsData;
        return nmcsPmcsData;
    }, [nmcsPmcsData, selectedTms]);

    const supplyMetrics = useMemo(
        () => computeAwaitingPartsMetrics(filteredNmcs),
        [filteredNmcs]
    );

    const mcTarget = MC_READINESS_GOAL;
    const mcDelta = counts.MC - mcTarget;
    const gaugeMin = MC_READINESS_GAUGE_MIN;
    const gaugeMax = MC_READINESS_GAUGE_MAX;

    useEffect(() => {
        let cancelled = false;
        const loadHistory = async () => {
            setHistoryLoading(true);
            try {
                const params = { days: 30 };
                if (selectedTms !== 'All') params.tms = selectedTms;
                const response = await api.get('/matcon-snapshots/', { params });
                const rows = Array.isArray(response.data) ? response.data : [];
                if (!cancelled) setHistorySnapshots(rows);
            } catch (err) {
                console.warn('Could not load MATCON snapshot history:', err);
                if (!cancelled) setHistorySnapshots([]);
            } finally {
                if (!cancelled) setHistoryLoading(false);
            }
        };
        loadHistory();
        return () => { cancelled = true; };
    }, [selectedTms]);

    useEffect(() => {
        let cancelled = false;
        const loadDates = async () => {
            try {
                const response = await api.get('/aircraft-status-snapshots/dates/', { params: { days: 90 } });
                const dates = Array.isArray(response.data) ? response.data : [];
                if (cancelled) return;
                setAvailableBriefDates(dates);
                if (dates.length > 0) {
                    setBriefDate((current) => (dates.includes(current) ? current : dates[0]));
                }
            } catch (err) {
                console.warn('Could not load aircraft status snapshot dates:', err);
                if (!cancelled) setAvailableBriefDates([]);
            }
        };
        loadDates();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        let cancelled = false;
        const priorDate = shiftDateString(briefDate, -7);

        const loadBrief = async () => {
            setBriefLoading(true);
            try {
                const [currentResponse, priorResponse] = await Promise.all([
                    api.get('/aircraft-status-snapshots/', { params: { date: briefDate } }),
                    api.get('/aircraft-status-snapshots/', { params: { date: priorDate } })
                        .catch(() => ({ data: [] })),
                ]);
                if (cancelled) return;
                const rows = Array.isArray(currentResponse.data) ? currentResponse.data : [];
                setSnapshotBriefRows(rows);
                setHasSnapshotForDate(rows.length > 0);
                setPriorWeekRows(Array.isArray(priorResponse.data) ? priorResponse.data : []);
            } catch (err) {
                console.warn('Could not load aircraft status brief:', err);
                if (cancelled) return;
                setSnapshotBriefRows([]);
                setHasSnapshotForDate(false);
                setPriorWeekRows([]);
            } finally {
                if (!cancelled) setBriefLoading(false);
            }
        };

        loadBrief();
        return () => { cancelled = true; };
    }, [briefDate]);

    const todayStr = localDateString();
    const briefMode = useMemo(() => {
        if (hasSnapshotForDate) return 'snapshot';
        if (briefDate === todayStr) return 'live';
        return 'empty';
    }, [hasSnapshotForDate, briefDate, todayStr]);

    const briefRows = useMemo(() => {
        const source = briefMode === 'snapshot'
            ? snapshotBriefRows
            : briefMode === 'live'
                ? liveRecordsToBriefRows(statusData)
                : [];
        return filterBriefRows(source, { tms: selectedTms });
    }, [briefMode, snapshotBriefRows, statusData, selectedTms]);

    const filteredPriorWeekRows = useMemo(
        () => filterBriefRows(priorWeekRows, { tms: selectedTms }),
        [priorWeekRows, selectedTms]
    );

    const briefSiteCounts = useMemo(() => countBriefBySite(briefRows), [briefRows]);
    const briefStatusCounts = useMemo(() => countBriefByStatus(briefRows), [briefRows]);
    const priorStatusCounts = useMemo(
        () => countBriefByStatus(filteredPriorWeekRows),
        [filteredPriorWeekRows]
    );
    const briefGroupCounts = useMemo(() => countBriefByGroup(briefRows), [briefRows]);
    const priorGroupCounts = useMemo(
        () => countBriefByGroup(filteredPriorWeekRows),
        [filteredPriorWeekRows]
    );
    const nestedBriefGroups = useMemo(() => buildBriefNestedGroups(briefRows), [briefRows]);

    const toggleBriefGroup = (groupKey) => {
        setExpandedBriefGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupKey)) next.delete(groupKey);
            else next.add(groupKey);
            return next;
        });
    };

    const toggleBriefStatus = (status) => {
        setExpandedBriefStatuses((prev) => {
            const next = new Set(prev);
            if (next.has(status)) next.delete(status);
            else next.add(status);
            return next;
        });
    };

    const deltaColorFor = (delta) => {
        if (delta == null) return undefined;
        if (delta > 0) return '#dc3545';
        if (delta < 0) return '#28a745';
        return theme === 'dark' ? '#888' : '#666';
    };

    const briefModeLabel = briefMode === 'snapshot'
        ? 'Snapshot'
        : briefMode === 'live'
            ? 'Live (not yet snapshotted)'
            : 'No snapshot for this date';

    const historyTrend = useMemo(
        () => buildTrendFromSnapshots(historySnapshots),
        [historySnapshots]
    );

    const mcChange = useMemo(() => {
        const fromServer = computeMcChangeFromSnapshots(historySnapshots, counts.MC);
        if (fromServer != null) return fromServer;
        return computeMcChange(counts.MC, loadMcChangeSnapshot());
    }, [historySnapshots, counts.MC]);

    const trendData = useMemo(
        () => historyTrend || generateSimulatedTrend(counts),
        [historyTrend, counts]
    );
    const trendsAreSimulated = !historyTrend;

    const matconBarData = {
        labels: MATCON_CATEGORIES.map((c) => c.label),
        datasets: [{
            label: 'Count',
            data: MATCON_CATEGORIES.map((c) => counts[c.key]),
            backgroundColor: MATCON_CATEGORIES.map((c) => c.color),
            borderRadius: 4,
        }],
    };

    const trendLineData = {
        labels: trendData.labels,
        datasets: [
            {
                label: 'MC',
                data: trendData.datasets.MC,
                borderColor: '#28a745',
                backgroundColor: 'rgba(40, 167, 69, 0.1)',
                tension: 0.3,
                fill: false,
            },
            {
                label: 'NMCS',
                data: trendData.datasets.NMCS,
                borderColor: '#e83e8c',
                tension: 0.3,
                fill: false,
            },
            {
                label: 'NMCM-S',
                data: trendData.datasets['NMCM-S'],
                borderColor: '#17a2b8',
                tension: 0.3,
                fill: false,
            },
            {
                label: 'NMCM-U',
                data: trendData.datasets['NMCM-U'],
                borderColor: '#cd853f',
                tension: 0.3,
                fill: false,
            },
        ],
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: true,
        plugins: {
            legend: {
                labels: { color: theme === 'dark' ? '#e0e0e0' : '#333', boxWidth: 12, font: { size: 11 } },
            },
        },
        scales: theme === 'dark' ? {
            x: { ticks: { color: '#aaa', font: { size: 10 } }, grid: { color: '#333' } },
            y: { ticks: { color: '#aaa' }, grid: { color: '#333' }, beginAtZero: true },
        } : {
            y: { beginAtZero: true },
        },
    };

    const horizontalBarOptions = {
        ...chartOptions,
        indexAxis: 'y',
        plugins: { ...chartOptions.plugins, legend: { display: false } },
    };

    const matconUpdated = nmcsPmcsData.length > 0
        ? nmcsPmcsData.reduce((latest, row) => {
            const t = row.uploaded_at ? new Date(row.uploaded_at).getTime() : 0;
            return t > latest ? t : latest;
        }, 0)
        : null;

    const formatTimestamp = (d) =>
        d ? d.toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' }) : 'N/A';

    const loggedKpis = [
        { label: 'MC', value: counts.MC, bg: '#1a1a1a' },
        { label: '+/- Target', value: mcDelta, bg: mcDelta >= 0 ? '#28a745' : '#dc3545' },
        { label: 'RFT', value: counts.FMC, bg: '#495057' },
        { label: 'MC Change', value: mcChange != null ? (mcChange >= 0 ? `+${mcChange}` : mcChange) : '—', bg: '#28a745' },
        { label: 'Reporting', value: counts.reportingTotal, bg: '#007bff' },
        { label: 'NMCM-S', value: counts['NMCM-S'], bg: '#17a2b8' },
    ];

    const supplyKpis = [
        { label: 'Awaiting Parts', value: counts.NMCS, bg: '#fd7e14' },
        { label: 'Awaiting Parts Total', value: supplyMetrics.awaitingPartsTotal, bg: '#6c757d' },
        { label: 'Awaiting Parts No EDD', value: supplyMetrics.awaitingPartsNoEdd, bg: '#007bff' },
        { label: 'Awaiting Parts > 300 EDD', value: supplyMetrics.awaitingPartsOver300Edd, bg: '#17a2b8' },
    ];

    return (
        <div style={styles.wrapper}>
            <div style={styles.header}>
                <div>
                    <h2 style={styles.title}>Aircraft Report Summary</h2>
                    <div style={styles.subtitle}>
                        MC = UP + XC + FCF + DET · FMC = UP · NMCM-S = PM · NMCM-U = DN · NMCS = AWP · OOR = OTH, REI, ACI, AIMD
                    </div>
                </div>
                <div style={styles.filters}>
                    <div>
                        <div style={styles.filterLabel}>TMS</div>
                        <select style={styles.select} value={selectedTms} onChange={(e) => setSelectedTms(e.target.value)}>
                            {tmsOptions.map((t) => (
                                <option key={t} value={t}>{t}</option>
                            ))}
                        </select>
                    </div>
                </div>
                <div style={styles.timestamps}>
                    <div>BSR Data Last Refreshed: {formatTimestamp(lastRefreshed)}</div>
                    <div>MATCON Data Last Updated: {formatTimestamp(matconUpdated ? new Date(matconUpdated) : lastRefreshed)}</div>
                </div>
            </div>

            <div style={styles.topRow}>
                <div style={styles.card}>
                    <div style={styles.cardTitle}>MC Readiness (Current)</div>
                    <div style={styles.gaugeWrap}>
                        <McReadinessGauge mc={counts.MC} min={gaugeMin} max={gaugeMax} target={mcTarget} theme={theme} />
                        <div style={{ fontSize: '11px', color: theme === 'dark' ? '#888' : '#666', marginTop: '-8px' }}>
                            Target: {mcTarget} ({mcDelta >= 0 ? '+' : ''}{mcDelta})
                        </div>
                    </div>
                </div>

                <div style={styles.card}>
                    <div style={styles.cardTitle}>Aircraft Status Counts</div>
                    <Bar data={matconBarData} options={horizontalBarOptions} />
                </div>

                <div style={styles.card}>
                    <div style={styles.cardTitle}>Site Breakdown</div>
                    <div style={{ overflowX: 'auto' }}>
                        <table style={styles.table}>
                            <thead>
                                <tr>
                                    <th style={{ ...styles.th, textAlign: 'left' }}>MATCON</th>
                                    {siteKeys.map((s) => (
                                        <th key={s} style={styles.th}>{s}</th>
                                    ))}
                                    <th style={styles.th}>Total</th>
                                </tr>
                            </thead>
                            <tbody>
                                {MATCON_CATEGORIES.map((cat) => (
                                    <React.Fragment key={cat.key}>
                                        <tr>
                                            <td style={{ ...styles.td, ...styles.tdLabel }}>
                                                <span style={styles.matconLabelCell}>
                                                    {cat.key === 'OOR' && (
                                                        <button
                                                            type="button"
                                                            style={styles.expandBtn}
                                                            onClick={() => setOorExpanded((v) => !v)}
                                                            aria-expanded={oorExpanded}
                                                            aria-label={oorExpanded ? 'Collapse OOR details' : 'Expand OOR details'}
                                                        >
                                                            {oorExpanded ? '▼' : '▶'}
                                                        </button>
                                                    )}
                                                    {cat.label}
                                                </span>
                                            </td>
                                            {siteKeys.map((s) => (
                                                <td key={s} style={styles.td}>{siteBreakdown.bySite[s]?.[cat.key] ?? 0}</td>
                                            ))}
                                            <td style={{ ...styles.td, ...styles.tdTotal }}>{siteBreakdown.totals[cat.key]}</td>
                                        </tr>
                                        {cat.key === 'OOR' && oorExpanded && OOR_SUB_STATUSES.map((status) => (
                                            <tr key={`oor-${status}`}>
                                                <td style={{ ...styles.td, ...styles.tdSubLabel }}>{status}</td>
                                                {siteKeys.map((s) => (
                                                    <td key={s} style={styles.td}>{oorBreakdown.bySite[s]?.[status] ?? 0}</td>
                                                ))}
                                                <td style={{ ...styles.td, ...styles.tdTotal }}>{oorBreakdown.totals[status]}</td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                                <tr>
                                    <td style={{ ...styles.td, ...styles.tdLabel, ...styles.tdTotal }}>Total</td>
                                    {siteKeys.map((s) => (
                                        <td key={s} style={{ ...styles.td, ...styles.tdTotal }}>{siteBreakdown.bySite[s]?.total ?? 0}</td>
                                    ))}
                                    <td style={{ ...styles.td, ...styles.tdTotal }}>{siteBreakdown.totals.total}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div style={styles.bottomRow}>
                <div style={styles.card}>
                    <div style={styles.cardTitle}>Today&apos;s Logged Numbers</div>
                    <div style={styles.kpiRow}>
                        {loggedKpis.map((kpi) => (
                            <div key={kpi.label} style={styles.kpiCard(kpi.bg)}>
                                <div style={styles.kpiValue}>{kpi.value}</div>
                                <div style={styles.kpiLabel}>{kpi.label}</div>
                            </div>
                        ))}
                    </div>
                    {counts.unmapped > 0 && (
                        <div style={styles.trendNote}>
                            {counts.unmapped} aircraft with DS/DET/other statuses not in MATCON categories.
                        </div>
                    )}
                </div>

                <div style={styles.card}>
                    <div style={styles.cardTitle}>
                        {trendsAreSimulated ? 'Maintenance Trends (Simulated)' : 'Maintenance Trends'}
                    </div>
                    <Line data={trendLineData} options={chartOptions} />
                    <div style={styles.trendNote}>
                        {historyLoading && 'Loading snapshot history…'}
                        {!historyLoading && trendsAreSimulated && (
                            'Trend lines are simulated until daily snapshots exist. Snapshots run at 08:30 with the DSR (Excel export or: python manage.py snapshot_matcon_daily).'
                        )}
                        {!historyLoading && !trendsAreSimulated && (
                            'Trends from daily MATCON snapshots captured at 08:30 with the DSR.'
                        )}
                    </div>
                    <div style={styles.supplyKpiRow}>
                        {supplyKpis.map((kpi) => (
                            <div key={kpi.label} style={styles.kpiCard(kpi.bg)}>
                                <div style={styles.kpiValue}>{kpi.value}</div>
                                <div style={styles.kpiLabel}>{kpi.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div style={styles.briefCard}>
                <div style={styles.briefHeader}>
                    <div>
                        <h3 style={styles.briefTitle}>Daily Status Brief</h3>
                        <div style={styles.briefSubtitle}>
                            Expand MCRS / OOR, then status codes for BUNO + reason remarks.
                            {' · '}
                            {briefModeLabel}
                        </div>
                    </div>
                    <div>
                        <div style={styles.filterLabel}>Brief Date</div>
                        <input
                            type="date"
                            style={styles.select}
                            value={briefDate}
                            max={todayStr}
                            onChange={(e) => setBriefDate(e.target.value)}
                            list="aircraft-status-brief-dates"
                        />
                        <datalist id="aircraft-status-brief-dates">
                            {availableBriefDates.map((d) => (
                                <option key={d} value={d} />
                            ))}
                        </datalist>
                    </div>
                </div>

                {briefLoading && (
                    <div style={styles.emptyNote}>Loading daily status brief…</div>
                )}

                {!briefLoading && briefMode === 'empty' && (
                    <div style={styles.emptyNote}>
                        No snapshot for this date. Snapshots are captured at 08:30 with the DSR
                        (Excel export or: python manage.py snapshot_matcon_daily).
                    </div>
                )}

                {!briefLoading && briefMode !== 'empty' && (
                    <>
                        <div style={styles.siteStrip}>
                            {Object.keys(briefSiteCounts).sort().map((site) => (
                                <div key={site} style={styles.siteChip}>
                                    <div style={styles.siteChipLabel}>{site}</div>
                                    <div style={styles.siteChipValue}>{briefSiteCounts[site] || 0}</div>
                                    <div style={styles.siteChipLabel}>in brief statuses</div>
                                </div>
                            ))}
                        </div>

                        {nestedBriefGroups.map((group) => {
                            const groupCount = briefGroupCounts[group.key] || 0;
                            const hasPrior = filteredPriorWeekRows.length > 0;
                            const groupDelta = hasPrior
                                ? groupCount - (priorGroupCounts[group.key] || 0)
                                : null;
                            const groupExpanded = expandedBriefGroups.has(group.key);

                            return (
                                <div key={group.key} style={styles.statusGroup}>
                                    <button
                                        type="button"
                                        style={styles.statusGroupHeader}
                                        onClick={() => toggleBriefGroup(group.key)}
                                        aria-expanded={groupExpanded}
                                    >
                                        <span style={styles.expandBtn} aria-hidden="true">
                                            {groupExpanded ? '▼' : '▶'}
                                        </span>
                                        {group.label}
                                        <span style={styles.statusCount}>({groupCount})</span>
                                        {groupDelta != null && (
                                            <span style={{ ...styles.statusDelta, color: deltaColorFor(groupDelta) }}>
                                                {formatDelta(groupDelta)}
                                            </span>
                                        )}
                                    </button>

                                    {groupExpanded && group.statuses.map((child) => {
                                        const count = briefStatusCounts[child.status] || 0;
                                        const delta = hasPrior
                                            ? count - (priorStatusCounts[child.status] || 0)
                                            : null;
                                        const statusExpanded = expandedBriefStatuses.has(child.status);
                                        const aircraft = child.aircraft || [];

                                        return (
                                            <div key={`${group.key}-${child.status}`} style={styles.statusChildBlock}>
                                                <button
                                                    type="button"
                                                    style={styles.statusChildHeader}
                                                    onClick={() => toggleBriefStatus(child.status)}
                                                    aria-expanded={statusExpanded}
                                                >
                                                    <span style={styles.expandBtn} aria-hidden="true">
                                                        {statusExpanded ? '▼' : '▶'}
                                                    </span>
                                                    {child.label}
                                                    <span style={styles.statusCount}>({count})</span>
                                                    {delta != null && (
                                                        <span style={{ ...styles.statusDelta, color: deltaColorFor(delta) }}>
                                                            {formatDelta(delta)}
                                                        </span>
                                                    )}
                                                </button>
                                                {statusExpanded && (
                                                    aircraft.length === 0 ? (
                                                        <div style={styles.emptyNoteChild}>
                                                            No aircraft in {child.label} for this date.
                                                        </div>
                                                    ) : (
                                                        <div style={styles.aircraftTableWrap}>
                                                            <table style={styles.aircraftTable}>
                                                                <thead>
                                                                    <tr>
                                                                        <th style={styles.aircraftTh}>BUNO</th>
                                                                        <th style={styles.aircraftTh}>Site</th>
                                                                        <th style={styles.aircraftTh}>TMS</th>
                                                                        <th style={styles.aircraftTh}>Reason / Remarks</th>
                                                                        <th style={styles.aircraftTh}>EDD</th>
                                                                        <th style={styles.aircraftTh}>ECD</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {aircraft.map((row) => (
                                                                        <tr key={`${child.status}-${row.buno}`}>
                                                                            <td style={styles.aircraftTd}>{row.buno}</td>
                                                                            <td style={styles.aircraftTd}>
                                                                                {row.site_name || '—'}
                                                                            </td>
                                                                            <td style={styles.aircraftTd}>{row.tms_name || '—'}</td>
                                                                            <td style={styles.aircraftTd}>
                                                                                {row.reason?.trim() ? row.reason : '—'}
                                                                            </td>
                                                                            <td style={styles.aircraftTd}>{row.edd || '—'}</td>
                                                                            <td style={styles.aircraftTd}>{row.ecd || '—'}</td>
                                                                        </tr>
                                                                    ))}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    )
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </>
                )}
            </div>
        </div>
    );
}

export default MocDailyCallDashboard;
